import { useState, useEffect, useRef } from 'react'
import {
  UserPlus, Trash2, X, KeyRound, Users,
} from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { useUIStore } from '../stores/uiStore'
import { fetchUsers, createUser, updateUser, deleteUser, setUserPassword } from '../api/auth'
import { getLoginConfig } from '../api/entra'
import type { User } from '../types/auth'

const ROLES = ['Admin', 'Editor', 'Viewer'] as const

type AdminTab = 'users'

export default function AdminPage() {
  const [activeTab, setActiveTab] = useState<AdminTab>('users')

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Admin Panel</h1>

      {/* Tabs */}
      <div className="flex gap-1 border-b dark:border-gray-700">
        {[
          { id: 'users' as AdminTab, label: 'Users', icon: <Users size={15} /> },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-primary-600 text-primary-700 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'users' && <UsersTab />}
    </div>
  )
}

/* ── Users Tab ── */

function UsersTab() {
  const currentUser = useAuthStore((s) => s.user)
  const toast = useUIStore()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState({ username: '', display_name: '', role: 'Viewer' as const })
  const passwordRef = useRef<HTMLInputElement>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [pwModal, setPwModal] = useState<{ userId: number; username: string; tempPw: string } | null>(null)
  const [localPasswordLogin, setLocalPasswordLogin] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      const data = await fetchUsers()
      setUsers(data)
    } catch (err: unknown) {
      toast.showToast(err instanceof Error ? err.message : 'Failed to load users', 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    getLoginConfig().then((config) => {
      setLocalPasswordLogin(config.local_password_login)
    }).catch(() => {
      setLocalPasswordLogin(false)
    })
  }, [])

  const handleCreate = async () => {
    const password = passwordRef.current?.value ?? ''
    if (!form.username.trim() || !password.trim()) {
      setFormError('Username and password are required')
      return
    }
    if (password.length < 8) {
      setFormError('Password must be at least 8 characters')
      return
    }
    setFormError(null)
    try {
      await createUser({
        username: form.username.trim(),
        password,
        display_name: form.display_name.trim() || form.username.trim(),
        role: form.role,
      })
      setShowCreate(false)
      setForm({ username: '', display_name: '', role: 'Viewer' })
      if (passwordRef.current) passwordRef.current.value = ''
      toast.showToast('User created', 'success')
      load()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to create user'
      setFormError(msg)
    }
  }

  const handleUpdateRole = async (userId: number, newRole: typeof ROLES[number]) => {
    try {
      await updateUser(userId, { role: newRole })
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, role: newRole } : u))
      toast.showToast('Role updated', 'success')
    } catch (err: unknown) {
      toast.showToast(err instanceof Error ? err.message : 'Failed to update role', 'error')
    }
  }

  const handleToggleActive = async (userId: number, next: boolean) => {
    if (userId === currentUser?.id) {
      toast.showToast('You cannot deactivate your own account', 'error')
      return
    }
    try {
      await updateUser(userId, { is_active: next })
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, is_active: next } : u))
      toast.showToast(next ? 'User activated' : 'User deactivated', 'success')
    } catch (err: unknown) {
      toast.showToast(err instanceof Error ? err.message : 'Failed to update user', 'error')
    }
  }

  const handleDelete = async (userId: number) => {
    if (userId === currentUser?.id) {
      toast.showToast('You cannot delete your own account', 'error')
      return
    }
    if (!window.confirm('Delete this user?')) return
    try {
      await deleteUser(userId)
      setUsers((prev) => prev.filter((u) => u.id !== userId))
      toast.showToast('User deleted', 'success')
    } catch (err: unknown) {
      toast.showToast(err instanceof Error ? err.message : 'Failed to delete user', 'error')
    }
  }

  const handleResetPw = async (userId: number, username: string) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%&*'
    let tempPw = ''
    for (let i = 0; i < 12; i++) tempPw += chars.charAt(Math.floor(Math.random() * chars.length))
    try {
      await setUserPassword(userId, tempPw)
      setPwModal({ userId, username, tempPw })
    } catch (err: unknown) {
      toast.showToast(err instanceof Error ? err.message : 'Failed to reset password', 'error')
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Users</h2>
        {localPasswordLogin && <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-sm rounded-lg transition-colors"
        >
          <UserPlus size={14} /> Create User
        </button>}
      </div>

      {!localPasswordLogin && <p className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-100">
        New users should sign in with Microsoft first. They are added here with Viewer access, then you can assign their application role.
      </p>}

      {showCreate && (
        <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border dark:border-gray-700 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">New User</h3>
            <button onClick={() => { setShowCreate(false); setFormError(null) }} className="p-1 text-gray-400 hover:text-gray-600">
              <X size={14} />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input value={form.username} onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))} placeholder="Username" className="px-3 py-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            <input ref={passwordRef} placeholder="Password" type="password" autoComplete="new-password" className="px-3 py-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            <input value={form.display_name} onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))} placeholder="Display Name" className="px-3 py-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            <select value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as typeof f.role }))} className="px-3 py-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white">
              <option value="Viewer">Viewer</option>
              <option value="Editor">Editor</option>
              <option value="Admin">Admin</option>
            </select>
          </div>
          {formError && <p className="text-xs text-red-600">{formError}</p>}
          <button onClick={handleCreate} className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm rounded-lg transition-colors">Create</button>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b dark:border-gray-700 text-left text-xs text-gray-500 uppercase">
                <th className="py-2 px-3">Username</th>
                <th className="py-2 px-3">Display Name</th>
                <th className="py-2 px-3">Email</th>
                <th className="py-2 px-3">Role</th>
                <th className="py-2 px-3">Active</th>
                <th className="py-2 px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="py-2 px-3 text-gray-900 dark:text-white font-medium">{u.username}</td>
                  <td className="py-2 px-3 text-gray-600 dark:text-gray-300">{u.display_name}</td>
                  <td className="py-2 px-3 text-gray-600 dark:text-gray-300">{u.email || '—'}</td>
                  <td className="py-2 px-3">
                    <select
                      value={u.role}
                      onChange={(e) => handleUpdateRole(u.id, e.target.value as typeof ROLES[number])}
                      className="px-2 py-1 border rounded text-xs dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="Viewer">Viewer</option>
                      <option value="Editor">Editor</option>
                      <option value="Admin">Admin</option>
                    </select>
                  </td>
                  <td className="py-2 px-3">
                    <button
                      onClick={() => handleToggleActive(u.id, !u.is_active)}
                      disabled={u.id === currentUser?.id}
                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                        u.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      } disabled:opacity-40`}
                    >
                      {u.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="py-2 px-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {localPasswordLogin && <button
                        onClick={() => handleResetPw(u.id, u.username)}
                        className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-gray-100 rounded transition-colors"
                        title="Reset password"
                      >
                        <KeyRound size={14} />
                      </button>}
                      <button
                        onClick={() => handleDelete(u.id)}
                        disabled={u.id === currentUser?.id}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors disabled:opacity-40"
                        title="Delete user"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Reset password modal */}
      {pwModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={(e) => { if (e.target === e.currentTarget) setPwModal(null) }}>
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-5 w-full max-w-sm space-y-3">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Password reset for {pwModal.username}</h3>
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Temporary password:</p>
              <p className="text-sm font-mono text-gray-900 dark:text-white select-all">{pwModal.tempPw}</p>
            </div>
            <button onClick={() => setPwModal(null)} className="w-full py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors">Done</button>
          </div>
        </div>
      )}
    </div>
  )
}
